import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CardGrid } from 'src/components/cardGrid';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Snackbar } from 'src/components/snackbar';
import { StyledTypography } from 'src/components/subHeader/SubHeaderWithSorting.styled';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useModal } from 'src/hooks';
import { IAssembly, IFilter, IStatus, IStatusLabel } from 'src/interfaces';
import { DataTestID, lang, useUserRole } from 'src/utils';

import {
  AssemblyContainer,
  AssemblySectionHeader,
  CheckboxLabel,
} from '../addAssemblyTagPage/AddAssemblyTagPage.styled';
import { ALLOWED_REGEX } from '../addAssemblyTagPage/constants';
import { tagNameMaxLength, TestIds } from './constants';
import {
  CardGridWrapper,
  InputContainer,
  InputTextField,
  StyleDivider,
} from './EditAssemblyTagPage.styled';
import { useEditAssemblyTagPage } from './hooks/EditAssemblyTagPage.hook';
import { buildAssemblyTagAssignmentUpdates } from './modules/EditAssemblyTagPage.helper';

export const EditAssemblyTagPage = () => {
  const { tagId = '' } = useParams();
  const [assemblies, setAssemblies] = useState<IAssembly[]>([]);
  const [inputBoxStatus, setInputBoxStatus] = useState('');
  const [assemblyTagName, setAssemblyTagName] = useState('');
  const [selectedAssemblies, setSelectedAssemblies] = useState<IAssembly[]>([]);
  const [prevSelectedAssemblies, setPrevSelectedAssemblies] = useState<
    IAssembly[]
  >([]);
  const gridBottomRef = useRef<HTMLDivElement>(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isSelectAll, setIssSelectAll] = useState(false);
  const [singleSelect, setSingleSelect] = useState(false);

  const navigate = useNavigate();
  const CloseModal = useModal();
  const EditConfirmDialog = useModal();
  const { isViewOnly } = useUserRole();

  const [onChangeLoad, setOnChangeLoad] = useState<boolean>(false);

  const selectedCount = selectedAssemblies.length;

  const initialAssemblyIdsRef = useRef<Set<number>>(new Set());
  const initialTagNameRef = useRef<string>('');
  const lastTagIdRef = useRef<string>('');
  const lastPersistedAssemblyIdsRef = useRef<string>('');

  const {
    data,
    hasNextPage,
    fetchNextPage,
    getNextAssemblyPage,
    errorLoadingAssemblies,
    isLoadingAssemblies,
    isFetching,
    isFetchingNextPage,
    onChangeParams,
    updateAssemblyTag,
    updateAssemblies,
    selectedAssemblyTag,
    persistedSelectedAssemblies,
    isSelectedDataSuccess,
    isAssemblySuccess,
    selectedPageData,
    isSelectedPageDataSuccess,
  } = useEditAssemblyTagPage(Number(tagId));

  useEffect(() => {
    if (isSelectedDataSuccess)
      setPrevSelectedAssemblies(persistedSelectedAssemblies ?? []);
  }, [persistedSelectedAssemblies, isSelectedDataSuccess]);

  useEffect(() => {
    if (tagId !== lastTagIdRef.current) {
      lastTagIdRef.current = tagId;
      initialAssemblyIdsRef.current = new Set();
      initialTagNameRef.current = '';
      lastPersistedAssemblyIdsRef.current = '';
    }
  }, [tagId]);

  useEffect(() => {
    const list = persistedSelectedAssemblies ?? [];
    const idsSignature = list
      .map((a) => a.id)
      .sort((a, b) => a - b)
      .join(',');

    if (idsSignature !== lastPersistedAssemblyIdsRef.current) {
      lastPersistedAssemblyIdsRef.current = idsSignature;
      initialAssemblyIdsRef.current = new Set(list.map((a) => a.id));
    }
  }, [persistedSelectedAssemblies]);

  useEffect(() => {
    if (selectedAssemblyTag?.name) {
      if (tagId !== lastTagIdRef.current || !initialTagNameRef.current) {
        initialTagNameRef.current = selectedAssemblyTag.name;
        setAssemblyTagName(selectedAssemblyTag.name);
      }
    }
  }, [selectedAssemblyTag?.name, tagId]);

  useEffect(() => {
    if (
      !isSelectedPageDataSuccess ||
      !isAssemblySuccess ||
      !isSelectedDataSuccess ||
      !data
    )
      return;
    const shouldInstantlyFetchNextPage =
      assemblies.length &&
      gridBottomRef.current &&
      gridBottomRef.current.getBoundingClientRect().bottom <
        window.innerHeight &&
      hasNextPage;

    if (
      (!isSelectAll && !singleSelect) ||
      onChangeLoad ||
      shouldInstantlyFetchNextPage
    ) {
      const selectedIds = new Set(selectedPageData?.map((comp) => comp.id));
      const newlySelectedIds = new Set(
        selectedAssemblies?.map((comp) => comp.id),
      );
      const hasExtraSelection = [...newlySelectedIds].some(
        (id) => !selectedIds.has(id),
      );

      if (!hasExtraSelection && onChangeLoad) {
        const defaultSelected =
          selectedPageData?.filter((comp) =>
            prevSelectedAssemblies.some((prev) => prev.id === comp.id),
          ) ?? [];
        setSelectedAssemblies(defaultSelected);
      }

      const remainingAssemblies = data.pages.flatMap((page) => page.assemblies);
      setAssemblies([...(selectedPageData ?? []), ...remainingAssemblies]);
    }
    setSingleSelect(false);
    setIssSelectAll(false);
    setOnChangeLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    selectedPageData,
    isSelectedPageDataSuccess,
    isAssemblySuccess,
    isSelectedDataSuccess,
    prevSelectedAssemblies,
    onChangeLoad,
  ]);

  useEffect(() => {
    setOnChangeLoad(true);
    if (selectedAssemblies.length > 0) {
      setSelectedAssemblies((selectedAssemblies) => {
        const newAssemblies = prevSelectedAssemblies.filter((assembly) =>
          selectedAssemblies.some((selected) => selected.id === assembly.id),
        );

        return newAssemblies;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangeParams]);

  useEffect(() => {
    if (assemblies.length && gridBottomRef.current) {
      const { bottom } = gridBottomRef.current.getBoundingClientRect();
      const shouldInstantlyFetchNextPage =
        bottom < window.innerHeight && hasNextPage;

      if (shouldInstantlyFetchNextPage) {
        fetchNextPage();
      }
      setSelectAll(areAllAssembliesSelected(assemblies, selectedAssemblies));
    }
  }, [assemblies, fetchNextPage, hasNextPage, selectedAssemblies]);

  const totalCount =
    data?.pages?.[0]?.totalCount + selectedPageData?.length || 0;

  let numberofResults =
    data?.pages?.reduce((total, page) => {
      return total + (page.assemblies?.length || 0);
    }, 0) || 0;
  numberofResults += selectedPageData?.length || 0;

  const shouldDisplayResultInfo = numberofResults != null && totalCount != null;
  const resultCountText = `${lang.Showing} ${numberofResults}/${totalCount}`;

  const handleSelectAllClick = () => {
    const assemblyIds = new Set(assemblies.map((c) => c.id));
    const selectedDataIds = new Set(
      persistedSelectedAssemblies?.map((c) => c.id) || [],
    );
    if (selectAll) {
      setSelectedAssemblies([]);

      setPrevSelectedAssemblies((prev) =>
        prev.filter(
          (prevComp) =>
            !assemblyIds.has(prevComp.id) || !selectedDataIds.has(prevComp.id),
        ),
      );
    } else {
      setSelectedAssemblies(assemblies);

      setPrevSelectedAssemblies((prev) => {
        const prevIds = new Set(prev.map((c) => c.id));
        const toAdd = assemblies.filter(
          (comp) => selectedDataIds.has(comp.id) && !prevIds.has(comp.id),
        );

        return [...prev, ...toAdd];
      });
    }
    setIssSelectAll(true);
  };

  const handleAssemblyTagNameChange = useCallback((value: string) => {
    startTransition(() => {
      if (value.length > 100) {
        setInputBoxStatus(lang.characterLimit);
        setAssemblyTagName(value);

        return;
      }

      if (!ALLOWED_REGEX.test(value)) {
        setInputBoxStatus(lang.invalidTagName);
        setAssemblyTagName(value);

        return;
      }

      setAssemblyTagName(value);

      const trimValue = value.trim();
      if (trimValue === initialTagNameRef.current || trimValue === '') {
        setInputBoxStatus('');
      } else {
        setInputBoxStatus('changed');
      }
    });
  }, []);

  const handleAssemblyCheckboxChange = useCallback(
    (assembly: IAssembly) => {
      setSingleSelect(true);
      const isSelected = selectedAssemblies.some(
        (selectedAssembly) => selectedAssembly.id === assembly.id,
      );

      const isPrevSelectedAssembly = persistedSelectedAssemblies?.some(
        (selectedAssembly) => selectedAssembly.id === assembly.id,
      );

      if (isPrevSelectedAssembly && !isSelected) {
        setPrevSelectedAssemblies((prev) => {
          const exists = prev.some((c) => c.id === assembly.id);
          if (exists) return prev;

          return [...prev, assembly];
        });
      } else if (isPrevSelectedAssembly && isSelected) {
        setPrevSelectedAssemblies(
          prevSelectedAssemblies.filter(
            (prevSelected) => prevSelected.id !== assembly.id,
          ),
        );
      }

      const updatedSelectedAssemblies = isSelected
        ? selectedAssemblies.filter(
            (selectedAssembly) => selectedAssembly.id !== assembly.id,
          )
        : [...selectedAssemblies, assembly];

      setSelectedAssemblies(updatedSelectedAssemblies);
      setSelectAll(
        areAllAssembliesSelected(assemblies, updatedSelectedAssemblies),
      );
    },
    [
      assemblies,
      setSelectedAssemblies,
      setPrevSelectedAssemblies,
      prevSelectedAssemblies,
      selectedAssemblies,
      persistedSelectedAssemblies,
      setSelectAll,
    ],
  );

  const areAllAssembliesSelected = (
    assemblies: IAssembly[],
    selected: IAssembly[],
  ) => {
    return assemblies.every((assembly) =>
      selected.some((selectedAssembly) => selectedAssembly.id === assembly.id),
    );
  };
  const handleModalConfirm = () => {
    navigate(PATHS.ASSEMBLY_MANAGE_TAGS);
  };

  const hasCheckboxesChanged = useMemo(() => {
    const currentIds = new Set(
      selectedAssemblies.map((assembly) => assembly.id),
    );
    const initialIds = initialAssemblyIdsRef.current;

    return (
      currentIds.size !== initialIds.size ||
      !Array.from(currentIds).every((id) => initialIds.has(id))
    );
  }, [selectedAssemblies]);

  const hasNameChanged = useMemo(() => {
    const trimmedName = assemblyTagName.trim();
    const hasValidationError =
      inputBoxStatus === lang.uniqueTagAlert ||
      inputBoxStatus === lang.characterLimit ||
      inputBoxStatus === lang.invalidTagName;

    return (
      trimmedName !== '' &&
      trimmedName !== initialTagNameRef.current &&
      !hasValidationError
    );
  }, [assemblyTagName, inputBoxStatus]);

  const isSaveDisabled = useMemo(() => {
    const trimmedName = assemblyTagName.trim();
    const hasValidationError =
      inputBoxStatus === lang.uniqueTagAlert ||
      inputBoxStatus === lang.characterLimit ||
      inputBoxStatus === lang.invalidTagName;

    return (
      !(hasNameChanged || hasCheckboxesChanged) ||
      hasValidationError ||
      trimmedName === ''
    );
  }, [hasNameChanged, hasCheckboxesChanged, inputBoxStatus, assemblyTagName]);

  const isInitialListLoading = useMemo(
    () =>
      isLoadingAssemblies ||
      !isSelectedDataSuccess ||
      !isSelectedPageDataSuccess ||
      !isAssemblySuccess,
    [
      isLoadingAssemblies,
      isSelectedDataSuccess,
      isSelectedPageDataSuccess,
      isAssemblySuccess,
    ],
  );

  const isListLoading = useMemo(
    () => isInitialListLoading || (isFetching && assemblies.length === 0),
    [isInitialListLoading, isFetching, assemblies.length],
  );

  const isSaving = updateAssemblyTag.isLoading || updateAssemblies.isLoading;

  const handleSave = async () => {
    const assemblyTagForSave = assemblyTagName.trim();

    try {
      if (hasNameChanged) {
        const request = {
          assemblyTagId: tagId,
          assemblyTag: assemblyTagForSave,
        };
        await updateAssemblyTag.mutateAsync(request);
      }

      if (hasCheckboxesChanged) {
        const updatedAssemblies = buildAssemblyTagAssignmentUpdates(
          selectedAssemblies,
          persistedSelectedAssemblies,
          Number(tagId),
        );

        if (updatedAssemblies.length > 0) {
          await updateAssemblies.mutateAsync(updatedAssemblies);
        }
      }

      if (hasNameChanged || hasCheckboxesChanged) {
        navigate(PATHS.ASSEMBLY_MANAGE_TAGS);
        Snackbar.success(`${assemblyTagForSave} ${lang.successfullyUpdated}`);
      }
    } catch (error: any) {
      if (
        error.response?.data?.error?.includes(
          'An item with the same unique field already exists',
        )
      ) {
        setInputBoxStatus(lang.uniqueTagAlert);
      } else {
        Snackbar.error(lang.failedSaveText);
      }
    }
  };

  const filters: IFilter[] = [
    {
      label: 'Status',
      options: Object.values(IStatus).map((value) => ({
        value: value,
        label: IStatusLabel[`${value}`],
      })),
    },
    {
      label: lang.assemblyTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
    },
  ];

  const { onChangeKeyword, onRemoveKeyword, onReset } =
    useFilterAndSorting(filters);

  return (
    <Grid>
      <Header
        backLink={-1}
        titlePrefix={lang.edit}
        title={assemblyTagName}
        breadcrumbItems={[
          {
            label: lang.assemblies,
            link: `${PATHS.COMPONENTS}`,
          },
          {
            label: lang.tags,
            link: `${PATHS.ASSEMBLY_MANAGE_TAGS}`,
          },
          {
            label: lang.editTag,
          },
        ]}
        actionBar={isViewOnly && <ViewOnlyAlert />}
      />
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeParams}
            showReset
            onReset={onReset}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Typography variant="h4" component="h2" gutterBottom>
            Tag details
          </Typography>
          <StyleDivider />
          <InputContainer>
            <InputTextLabel>{lang.tagName}</InputTextLabel>
            <InputTextField
              variant="outlined"
              inputProps={{
                maxLength: tagNameMaxLength,
                'data-testid': TestIds.TAG_NAME_INPUT,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleAssemblyTagNameChange(event.target.value)
              }
              onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleAssemblyTagNameChange(event.target.value)
              }
              value={assemblyTagName}
              error={
                !!inputBoxStatus &&
                inputBoxStatus !== 'changed' &&
                inputBoxStatus !== ''
              }
              helperText={inputBoxStatus === 'changed' ? '' : inputBoxStatus}
            />
            {shouldDisplayResultInfo && (
              <StyledTypography paddingTop={4}>
                {resultCountText}
              </StyledTypography>
            )}
          </InputContainer>
          <AssemblyContainer>
            {assemblies.length > 0 && !isListLoading && (
              <AssemblySectionHeader>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  flexGrow={1}>
                  {lang.selectAssemblies}
                </Typography>
                <Typography
                  variant="body1"
                  color="grey"
                  marginRight={1}
                  marginBottom={0}
                  paddingTop={1}>
                  {`${selectedCount} Selected`}
                </Typography>
                <Button variant="text" onClick={handleSelectAllClick}>
                  {selectAll ? lang.deselectAll : lang.selectAll}
                </Button>
              </AssemblySectionHeader>
            )}
            <StyleDivider />
            <CardGridWrapper>
              {isListLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    minHeight: '100%',
                    pb: 3,
                    boxSizing: 'border-box',
                  }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <NoResultAlert
                    isLoading={isListLoading}
                    dataToCheck={assemblies}
                  />
                  <CardGrid
                    isLoading={isListLoading}
                    error={errorLoadingAssemblies}
                    updatePage={getNextAssemblyPage}>
                    {assemblies.map((assembly: IAssembly) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={assembly.id}
                        data-testid={DataTestID.AddAssemblyTagAssemblyData}>
                        <CheckboxLabel
                          label={`${assembly.name}`}
                          control={
                            <Checkbox
                              checked={selectedAssemblies.some(
                                (selectedAssembly) =>
                                  selectedAssembly.id === assembly.id,
                              )}
                              onChange={() =>
                                handleAssemblyCheckboxChange(assembly)
                              }
                            />
                          }
                        />
                      </Grid>
                    ))}
                  </CardGrid>
                  {isFetchingNextPage && (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </>
              )}
              <div ref={gridBottomRef}></div>
            </CardGridWrapper>
          </AssemblyContainer>
        </Grid>
      </Grid>
      <SimpleDialog
        open={CloseModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={CloseModal.onCloseModal}
        onClickCancel={CloseModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />
      {!isViewOnly && (
        <Footer
          showFooterActions
          onCancel={CloseModal.onOpenModal}
          onSave={EditConfirmDialog.onOpenModal}
          cancelLabel={lang.close}
          disableSave={isSaveDisabled}
          isSaving={isSaving}
        />
      )}
      <SimpleDialog
        open={EditConfirmDialog.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.assemblyTagSaveModal.title}
        subTitle={lang.assemblyTagSaveModal.subTitle}
        onClose={EditConfirmDialog.onCloseModal}
        onClickCancel={EditConfirmDialog.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleSave}
      />
    </Grid>
  );
};
